// ✅ FINAL — CameraCaptureModal.jsx with Timer, Flash, Auto-Send, and Defensive Handling
import React, { useEffect, useRef, useState } from "react";

const COUNTDOWN_SECONDS = 3; // Change to 0 to disable countdown
const AUTO_SEND = true; // Set to false if you want manual "Send" after preview

const CameraCaptureModal = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const flashRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [preview, setPreview] = useState(null);
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    let active = true;

    const startCamera = async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
        if (active) {
          setStream(mediaStream);
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
          }
        }
      } catch (err) {
        console.error("Camera error:", err);
        alert("Unable to access the camera. Please check permissions.");
        onClose();
      }
    };

    startCamera();

    return () => {
      active = false;
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [onClose, stream]);

  const doCapture = () => {
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) throw new Error("Camera or canvas not available.");

      const context = canvas.getContext("2d");
      if (!context) throw new Error("Canvas context is not available.");

      canvas.width = video.videoWidth || 320;
      canvas.height = video.videoHeight || 240;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      flashRef.current.style.display = "block";
      setTimeout(() => {
        flashRef.current.style.display = "none";
      }, 100);

      canvas.toBlob(
        (blob) => {
          if (blob) {
            try {
              const file = new File([blob], "webcam-photo.jpg", { type: "image/jpeg" });
              const previewURL = URL.createObjectURL(blob);
              if (AUTO_SEND) {
                onCapture(file);
                onClose();
              } else {
                setPreview({ file, previewURL });
              }
            } catch (e) {
              console.error("Preview creation failed:", e);
              alert("Failed to prepare the image.");
            }
          } else {
            alert("Failed to capture image.");
          }
        },
        "image/jpeg",
        0.95
      );
    } catch (e) {
      console.error("Capture failed:", e);
      alert("Error capturing image.");
    }
  };

  const handleCapture = () => {
    if (COUNTDOWN_SECONDS > 0) {
      let seconds = COUNTDOWN_SECONDS;
      setCountdown(seconds);
      const interval = setInterval(() => {
        seconds -= 1;
        setCountdown(seconds);
        if (seconds <= 0) {
          clearInterval(interval);
          setCountdown(null);
          doCapture();
        }
      }, 1000);
    } else {
      doCapture();
    }
  };

  const handleSend = () => {
    if (preview?.file) {
      onCapture(preview.file);
      onClose();
    }
  };

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div ref={flashRef} style={flashStyle} />
        {!preview ? (
          <>
            {countdown !== null ? (
              <div style={countdownStyle}>{countdown}</div>
            ) : (
              <>
                <video ref={videoRef} autoPlay playsInline muted style={videoStyle} />
                <canvas ref={canvasRef} style={{ display: "none" }} />
                <div style={buttonRowStyle}>
                  <button onClick={handleCapture} style={buttonStyle}>📸 Capture</button>
                  <button onClick={onClose} style={{ ...buttonStyle, backgroundColor: "#999" }}>Cancel</button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <img src={preview.previewURL} alt="Captured" style={videoStyle} />
            <div style={buttonRowStyle}>
              <button onClick={handleSend} style={buttonStyle}>✅ Send</button>
              <button onClick={() => setPreview(null)} style={{ ...buttonStyle, backgroundColor: "#999" }}>Retake</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ✅ Inline Styles
const overlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 2000,
};

const modalStyle = {
  backgroundColor: "#fff",
  padding: 20,
  borderRadius: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  textAlign: "center",
  position: "relative",
};

const videoStyle = {
  width: 320,
  height: 240,
  borderRadius: 8,
  marginBottom: 16,
  objectFit: "cover",
};

const buttonRowStyle = {
  display: "flex",
  justifyContent: "center",
  gap: 10,
};

const buttonStyle = {
  padding: "8px 16px",
  borderRadius: 6,
  border: "none",
  backgroundColor: "#007bff",
  color: "#fff",
  cursor: "pointer",
};

const countdownStyle = {
  fontSize: "72px",
  fontWeight: "bold",
  color: "#333",
};

const flashStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "#fff",
  opacity: 0.8,
  display: "none",
  zIndex: 5,
};

export default CameraCaptureModal;
